import { Box } from "@mui/material";
import { createTheme, styled } from "@mui/material/styles";

// Importe no index.html:
// <link href="https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@400;500;600;700&family=Montserrat:wght@300;400;500;600&display=swap" rel="stylesheet">

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    notification: true;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: "#2C5282",
      light: "#4A69BB",
      dark: "#1A365D",
    },
    secondary: {
      main: "#744210",
      light: "#975A16",
      dark: "#5F370E",
    },
    background: {
      default: "#F7FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#2D3748",
      secondary: "#4A5568",
    },
    success: {
      main: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Crimson Pro", serif',
      fontWeight: 700,
      fontSize: "2.5rem",
      letterSpacing: "-0.01em",
    },
    h2: {
      fontFamily: '"Crimson Pro", serif',
      fontWeight: 600,
      fontSize: "2rem",
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: '"Crimson Pro", serif',
      fontWeight: 600,
      fontSize: "1.75rem",
      letterSpacing: "-0.01em",
    },
    h4: {
      fontFamily: '"Crimson Pro", serif',
      fontWeight: 600,
      fontSize: "1.5rem",
      letterSpacing: "-0.01em",
    },
    button: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 500,
      letterSpacing: "0.02em",
    },
    body1: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          padding: "8px 24px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          "& .MuiToolbar-root": {
            borderRadius: 0,
          },
          "& .MuiPaper-root": {
            borderRadius: 0,
          },
        },
      },
    },
  },
});


const yellowPallete = {
  palette: {
    primary: {
      main: "#FFF",
      light: "#FFECB3", // Amarelo claro suave (Amber 100)
      dark: "#FFA000", // Amarelo escuro/dourado (Amber 700)
      contrastText: "#000000", // Contraste forte para legibilidade
    },
    secondary: {
      main: "#FF9800", // Laranja quente (Orange 500)
      light: "#FFCC80", // Laranja suave (Orange 200)
      dark: "#F57C00", // Laranja profundo (Orange 700)
      contrastText: "#000000",
    },
    success: {
      main: "#BFE700", // Dourado vibrante
      light: "#BFEA80",
      dark: "#A6A700",
      contrastText: "#000000",
    },
    error: {
      main: "#B10807", // Laranja avermelhado (Deep Orange 600)
      light: "#FF9E80",
      dark: "#DD2C00",
      contrastText: "#000000",
    },
    warning: {
      main: "#FFC400", // Amarelo alaranjado (Amber 600)
      light: "#FFE082",
      dark: "#FFAB00",
      contrastText: "#000000",
    },
    info: {
      main: "#FFD54F", // Amarelo pastel
      light: "#FFF9C4",
      dark: "#FFB300",
      contrastText: "#000000",
    },
    background: {
      default: "#121212", // Preto fosco (para fundo escuro moderno)
      paper: "#1E1E1E", // Cinza escuro para contraste elegante
    },
    text: {
      primary: "#FFF", // Amarelo neon para máxima visibilidade
      secondary: "#FFF", // Amarelo quente
      disabled: "#9E9E9E", // Cinza médio para textos desativados
    },
    divider: "#FFD700", // Dourado elegante para divisórias
  },
};

export const WizardStyledBoxContainer = styled(Box)(({ theme }) => ({
  borderRadius: "1rem",
  padding: "8px",
  backgroundColor: yellowPallete.palette.background.default,
}));

export const WizardTheme = createTheme({
  ...theme,
  typography: {
    ...theme.typography,
    h4: {
      ...theme.typography.h4,
      color: yellowPallete.palette.primary.main,
    },
    h6: {
      ...theme.typography.h6,
      color: yellowPallete.palette.primary.light,
    },
  },
  palette: {
    ...yellowPallete.palette,
  },
  components: {
    ...theme.components,
    MuiTypography: {
      styleOverrides: {
        root: {
          color: yellowPallete.palette.primary.main,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          background: "rgba(0,0,0,0.8)",
          "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
          {
            WebkitAppearance: "none",
          },
        },
      },
    },
    MuiButton: {
      ...theme.components?.MuiButton,
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: "bold",
          borderColor: "#FFF",
          color: "#FFF",
        },
        containedPrimary: {
          backgroundColor: "#FFC107",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        },
        outlinedPrimary: {
          borderColor: "#FFF",
          color: "#FFF",
          "&:hover": {
            borderColor: "#FFA000",
            color: "#FFA000",
          },
          variants: [
            {
              props: { variant: "notification" },
              style: {
                color: yellowPallete.palette.primary.main,
                size: "small",
                border: "1px solid",
                borderColor: yellowPallete.palette.primary.main,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: yellowPallete.palette.primary.dark,
                },
              },
            },
          ],
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "1rem",
          marginBottom: "1rem",
          padding: "0",
          backgroundColor: yellowPallete.palette.background.default,
          border: "1px solid #FFF",
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: "auto",
          backgroundColor: yellowPallete.palette.background.default,
          boxShadow: "1px 1px 5px " + yellowPallete.palette.background.paper,
          "& .MuiStep-root": {
            padding: "0 5px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
            },
          },
          "& .MuiStepLabel-root": {
            marginBottom: "0.5rem",
            marginTop: "0",
            flexDirection: "column",
            alignItems: "center",

          },
          "& .MuiStepLabel-iconContainer": {
            paddingRight: "0",
          },
          "& .MuiStepConnector-root": {
            top: 17,
            minWidth: 15,
            "& .MuiStepConnector-line": {
              borderColor: yellowPallete.palette.primary.main,
              borderTopWidth: 3,
              borderRadius: 4,
              transition: "all 0.3s ease",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            },
          },
          "& .MuiStepConnector-active": {
            "& .MuiStepConnector-line": {
              borderColor: yellowPallete.palette.primary.main,
              borderTopStyle: "solid",
            },
          },
          "& .MuiStepConnector-completed": {
            "& .MuiStepConnector-line": {
              borderColor: yellowPallete.palette.primary.light,
              borderTopStyle: "solid",
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "1rem",
          marginBottom: "1rem",
          padding: "4px",
          backgroundColor: yellowPallete.palette.background.default,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0,0,0,0.6)", // Cor de fundo amarelo vibrante
          color: "#000", // Cor do texto para contraste
          borderRadius: "8px", // Borda arredondada
          "&.Mui-expanded": {
            backgroundColor: "rgba(0,0,0,0.4)", // Cor diferente quando expandido
          },
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.9)", // Efeito ao passar o mouse
          },
        },
        content: {
          fontWeight: "bold", // Deixa o texto mais destacado
          "&.Mui-expanded": {},
        },
        expandIconWrapper: {
          color: "#000", // Cor do ícone
          "&.Mui-expanded": {
            transform: "rotate(180deg)", // Animação para rotação
          },
        },
      },
    },
  },
});
